/**
 * Root translation key of everything the uploads service owns.
 */
const T_KEY = 'UPLOAD';

/**
 * Translation keys of the upload batch and upload item statuses.
 *
 * They live with the service and not with a screen, because the same
 * wording is shown wherever an upload batch is rendered, whatever
 * feature produced it.
 */
export const UploadTranslateMapper = {

    /**
     * Status wording. Note that the keys are the display words and not
     * the server ones: `Processed` reads as "Completed" and `Pending`
     * reads as "Processing".
     */
    STATUS: {

        /** Shown for a finished batch and for a `Processed` item. */
        COMPLETED: () => `${T_KEY}.STATUS.COMPLETED`,

        /** Shown for a `Failed` item. */
        FAILED: () => `${T_KEY}.STATUS.FAILED`,

        /** Shown for an unfinished batch and for a `Pending` item. */
        PROCESSING: () => `${T_KEY}.STATUS.PROCESSING`,

        /** Shown for a status word the app does not know. */
        UNKNOWN: () => `${T_KEY}.STATUS.UNKNOWN`,

    },

    /**
     * Wording of the toast fired when a batch the watcher was waiting on
     * has finished.
     *
     * The whole sentence is written here rather than taken from the
     * answer: the server words its summaries in English only, and a
     * summary cannot be styled by severity — a batch with a rejected file
     * in it would arrive worded as a failure and coloured as a success.
     *
     * Every title names what was uploaded, since the toast can reach a
     * user who has long since left the screen they uploaded from.
     */
    NOTIFICATION: {

        /**
         * Wording of what produced the batch, filled into every title.
         *
         * The server sends its own wording in `batch_type`, which is
         * English whatever the app is set to, so it is mapped to one of
         * these and only used as it stands when it maps to nothing.
         */
        TYPE: {

            /** A batch of energy bill invoices. */
            ENERGY_BILL: () => `${T_KEY}.NOTIFICATION.TYPE.ENERGY_BILL`,

            /** A batch whose type the app has no wording for. */
            DEFAULT: () => `${T_KEY}.NOTIFICATION.TYPE.DEFAULT`,

            /**
             * The key of one type by the server's own wording for it,
             * normalized. Resolves to nothing for a type the app has no
             * wording for, which is what `DEFAULT` is there to answer.
             *
             * @param type - The server's `batch_type`, upper-cased with
             * everything but letters and digits turned into underscores.
             */
            OF: (type: string) => `${T_KEY}.NOTIFICATION.TYPE.${type}`,

        },

        /** Title shown when every file of the batch was imported. */
        SUCCESS_TITLE: () => `${T_KEY}.NOTIFICATION.SUCCESS_TITLE`,

        /** Body shown when every file of the batch was imported. */
        SUCCESS_BODY: () => `${T_KEY}.NOTIFICATION.SUCCESS_BODY`,

        /** Title shown when some of the files were rejected. */
        PARTIAL_TITLE: () => `${T_KEY}.NOTIFICATION.PARTIAL_TITLE`,

        /** Body shown when some of the files were rejected. */
        PARTIAL_BODY: () => `${T_KEY}.NOTIFICATION.PARTIAL_BODY`,

        /** Title shown when none of the files could be imported. */
        FAILED_TITLE: () => `${T_KEY}.NOTIFICATION.FAILED_TITLE`,

        /** Body shown when none of the files could be imported. */
        FAILED_BODY: () => `${T_KEY}.NOTIFICATION.FAILED_BODY`,

        /**
         * Appended to a toast that can be tapped to reach the batch it
         * is about. Kept apart from the bodies so a toast that leads
         * nowhere never promises that it does.
         */
        TAP_HINT: () => `${T_KEY}.NOTIFICATION.TAP_HINT`,

    },

    /**
     * Wording of the navbar menu listing what is still uploading.
     *
     * It lives here beside the toast rather than with the navbar, because
     * the two say the same thing at different moments — the menu while a
     * batch runs, the toast once it is done — and their wording of a type
     * has to agree. The menu reuses `NOTIFICATION.TYPE` for that reason.
     */
    WATCH_MENU: {

        /** Heading of the open menu. */
        TITLE: () => `${T_KEY}.WATCH_MENU.TITLE`,

        /** Named for screen readers on the navbar button itself. */
        ARIA_LABEL: () => `${T_KEY}.WATCH_MENU.ARIA_LABEL`,

        /** How far a batch has got, e.g. "3 of 10 files". */
        PROGRESS: () => `${T_KEY}.WATCH_MENU.PROGRESS`,

        /**
         * Shown in place of the counts until the first round answers,
         * which is a few seconds at most.
         */
        PENDING: () => `${T_KEY}.WATCH_MENU.PENDING`,

        /**
         * How many files have been rejected so far. Shown only once there
         * are any, so a batch going well says nothing about failures.
         */
        REJECTED: () => `${T_KEY}.WATCH_MENU.REJECTED`,

        /**
         * Says a row can be opened. Only rendered for a batch that was
         * watched with somewhere to go.
         */
        VIEW: () => `${T_KEY}.WATCH_MENU.VIEW`,

    },

} as const;
